/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

@Injectable()
export class TesterNavigationSidebarExpansionService {
  readonly itemOpened$ = new Subject<symbol>();

  /** Emits when the bottom toggle button is clicked. The service picks the
   *  target itself and emits its id (or null = close any open flyout). Each
   *  menu-item subscribes and only acts when the emitted id matches its own
   *  (or on null, when it is the currently-expanded one). */
  private readonly toggleActiveSubject = new Subject<symbol | null>();
  readonly toggleActive$: Observable<symbol | null> = this.toggleActiveSubject.asObservable();

  /** Emits when the sidebar's document-click handler detects a click outside
   *  the sidebar. Every menu-item subscribes and closes its own flyout. */
  private readonly closeAllRequestedSubject = new Subject<void>();
  readonly closeAllRequested$: Observable<void> = this.closeAllRequestedSubject.asObservable();

  /** True while any menu-item flyout is open. Used to swap the toggle icon
   *  between the align-right (closed) and align-left (open) glyphs. */
  private readonly anyExpandedSubject = new BehaviorSubject<boolean>(false);
  readonly anyExpanded$: Observable<boolean> = this.anyExpandedSubject.asObservable();

  private readonly expandedItems = new Set<symbol>();
  /** hasChildren menu-items in registration (template) order. The first
   *  entry is the toggle button's fallback target when no row is active. */
  private readonly registeredHasChildren: symbol[] = [];
  /** Subset of registeredHasChildren that are currently active. */
  private readonly activeHasChildrenItems = new Set<symbol>();

  registerHasChildren(id: symbol): void {
    if (!this.registeredHasChildren.includes(id)) {
      this.registeredHasChildren.push(id);
    }
  }

  unregisterHasChildren(id: symbol): void {
    const index = this.registeredHasChildren.indexOf(id);
    if (index >= 0) {
      this.registeredHasChildren.splice(index, 1);
    }
    this.activeHasChildrenItems.delete(id);
    // Defensive: if a destroyed row left a stale expanded entry behind,
    // drop it and resync anyExpanded$ so the toggle button icon and the
    // "anything expanded?" check stay accurate.
    if (this.expandedItems.delete(id)) {
      this.updateAnyExpanded();
    }
  }

  reportActive(id: symbol, active: boolean): void {
    if (active) {
      this.activeHasChildrenItems.add(id);
    } else {
      this.activeHasChildrenItems.delete(id);
    }
  }

  toggleActive(): void {
    // If anything is expanded, broadcast a close — the expanded item(s)
    // will collapse themselves on receiving null.
    if (this.expandedItems.size > 0) {
      this.toggleActiveSubject.next(null);
      return;
    }
    // Otherwise pick a target to expand. Prefer an active hasChildren row;
    // fall back to the first registered one so the toggle is still useful
    // in apps where no hasChildren section is active for the current route
    // (utest-fe outside the community area is the motivating case).
    const target = this.registeredHasChildren.find(id => this.activeHasChildrenItems.has(id))
      ?? this.registeredHasChildren[0];
    if (target !== undefined) {
      this.toggleActiveSubject.next(target);
    }
  }

  closeAll(): void {
    this.closeAllRequestedSubject.next();
  }

  reportExpanded(id: symbol, expanded: boolean): void {
    if (expanded) {
      this.expandedItems.add(id);
    } else {
      this.expandedItems.delete(id);
    }
    this.updateAnyExpanded();
  }

  private updateAnyExpanded(): void {
    const anyNow = this.expandedItems.size > 0;
    if (anyNow !== this.anyExpandedSubject.value) {
      this.anyExpandedSubject.next(anyNow);
    }
  }
}
